import React from 'react';

// --- Card ---
interface GlassCardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  noPadding?: boolean;
}

export const GlassCard: React.FC<GlassCardProps> = ({ children, className = '', onClick, noPadding = false }) => {
  return (
    <div 
      onClick={onClick}
      className={`bg-white/60 backdrop-blur-xl border border-white/50 shadow-lg rounded-3xl ${noPadding ? '' : 'p-5'} transition-all duration-300 ${onClick ? 'cursor-pointer hover:bg-white/70 active:scale-[0.99]' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

// --- Button ---
interface GlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon' | 'danger';
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const GlassButton: React.FC<GlassButtonProps> = ({ 
  children, 
  variant = 'primary', 
  fullWidth = false, 
  className = '', 
  size = 'md',
  ...props 
}) => {
  const baseStyles = "relative font-semibold transition-all duration-300 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variants = {
    primary: "bg-violet-600 text-white shadow-lg shadow-violet-300/50 hover:bg-violet-700 hover:shadow-violet-400/50",
    secondary: "bg-white text-slate-700 shadow-md hover:bg-slate-50 border border-slate-100",
    ghost: "bg-transparent text-slate-600 hover:bg-white/30",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-100",
    icon: "rounded-full bg-white/50 hover:bg-white text-slate-700 shadow-sm backdrop-blur-md aspect-square",
  };

  const sizes = {
    sm: "rounded-xl py-2 px-3 text-sm",
    md: "rounded-2xl py-3 px-6 text-base",
    lg: "rounded-2xl py-4 px-8 text-lg",
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const sizeClass = variant === 'icon' ? 'p-2' : sizes[size];

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${sizeClass} ${widthClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// --- Input ---
interface GlassInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
}

export const GlassInput: React.FC<GlassInputProps> = ({ className = '', icon, ...props }) => {
  return (
    <div className="relative">
      <input 
        className={`w-full bg-white/50 border border-white/60 rounded-2xl py-3 px-4 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:bg-white/80 transition-all ${icon ? 'pl-10' : ''} ${className}`}
        {...props}
      />
      {icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          {icon}
        </div>
      )}
    </div>
  );
};

// --- Avatar ---
interface AvatarProps {
  src: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({ src, alt, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-20 h-20',
  };

  return (
    <img 
      src={src} 
      alt={alt} 
      className={`rounded-full object-cover border-2 border-white shadow-sm ${sizeClasses[size]} ${className}`}
    />
  );
};

// --- Screen Container ---
interface ScreenContainerProps {
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}

export const ScreenContainer: React.FC<ScreenContainerProps> = ({ children, className = '', fullWidth = false }) => {
  return (
    <div className={`min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 ${className}`}>
      {/* On desktop, allow full width for dashboard, on mobile/auth keep it constrained unless overridden */}
      <div className={`h-full ${fullWidth ? 'w-full' : 'max-w-md mx-auto p-6'}`}>
        {children}
      </div>
    </div>
  );
};

// --- Header ---
interface HeaderProps {
  title: string;
  subtitle?: string;
  rightIcon?: React.ReactNode;
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({ title, subtitle, rightIcon, className = '' }) => {
  return (
    <div className={`flex justify-between items-center mb-6 ${className}`}>
      <div>
        {subtitle && <h2 className="text-slate-500 font-medium">{subtitle}</h2>}
        <h1 className="text-3xl font-bold text-slate-800">{title}</h1>
      </div>
      {rightIcon && (
        <GlassButton variant="icon">
          {rightIcon}
        </GlassButton>
      )}
    </div>
  );
};

// --- Desktop Sidebar Item ---
interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 mb-2
      ${active 
        ? 'bg-violet-600 text-white shadow-lg shadow-violet-300/50' 
        : 'text-slate-500 hover:bg-white/50 hover:text-violet-600'
      }`}
  >
    <div>{icon}</div>
    <span className="font-semibold">{label}</span>
  </button>
);